import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useState } from "react"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userReportPage } from "../constants/reportPages"
import {
  exportUsersAsCSV,
  getPaginationMeta,
  useGetUsersQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"
import { formatNullable } from "./reportFormatters"

const columns: GridColDef[] = [
  {
    field: "first_name",
    headerName: "First Name",
    width: 160,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.first_name),
  },
  {
    field: "last_name",
    headerName: "Last Name",
    width: 160,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.last_name),
  },
  {
    field: "email",
    headerName: "Email",
    width: 260,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.email),
  },
  {
    field: "uuid",
    headerName: "User UUID",
    width: 280,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.uuid),
  },
  {
    field: "membership_uuid",
    headerName: "Membership UUID",
    width: 280,
    sortable: false,
    valueGetter: (_value, row) => formatNullable(row.membership_uuid),
  },
  {
    field: "groups",
    headerName: "Groups",
    width: 320,
    sortable: false,
    valueGetter: (_value, row) => {
      const groups = row.groups || []

      if (groups.length === 0) {
        return "-"
      }

      return groups
        .map((group: { label?: string | null }) => group.label || "Untitled")
        .join(", ")
    },
  },
]

function UserReportPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  const queryParams = {
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetUsersQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const handleExportCSV = () => {
    exportUsersAsCSV({
      ...groupParams,
      org_id: activeOrgId,
    })
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userReportPage.pageTitle}
        description={userReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom={true}>
            Filters
          </Typography>
          <GroupFilterSelect
            organizationId={activeOrgId}
            value={selectedGroups}
            onChange={(value) => {
              setSelectedGroups(value)
              setPaginationModel((prev) => ({ ...prev, page: 0 }))
            }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error">Error loading users. Please try again.</Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.id ?? row.uuid}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default UserReportPage
